import { Group, LoadingOverlay, Modal, Stack, Text } from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import { Button } from "@tfg/ui"
import { AxiosError } from "axios"
import { useEffect, useState } from "react"
import z, { ZodIssue } from "zod"

import { InlineBanner } from "~/components/InlineBanner"
import { TraceabilityLaunchList } from "~/pages/Admin/Ingestion/ProductImport/IngestionList"
import { TraceabilityErrors } from "~/pages/Admin/Ingestion/ProductImport/components/TraceabilityErrors"
import { TraceabilitySuccessStats } from "~/pages/Admin/Ingestion/ProductImport/components/TraceabilitySuccessStats"
import { UploadOperationsForm } from "~/pages/Admin/Ingestion/ProductImport/components/UploadOperationsForm"
import {
  LaunchTraceabilityResponse,
  useLaunchTraceabilityMutation,
} from "~/pages/Admin/Ingestion/ProductImport/hooks/useLaunchTraceabilityMutation"
import { ErrorLogs } from "~/pages/Admin/Ingestion/ProductImport/hooks/useVerifyProductImportMutation"

type Props = {
  modalOpened: boolean
  drawerActions: {
    open: () => void
    close: () => void
  }
  traceability: TraceabilityLaunchList
}

export function TraceabilityModal({
  modalOpened,
  traceability,
  drawerActions,
}: Props) {
  const [traceabilityState, setTraceabilityState] =
    useState<TraceabilityLaunchList>(traceability)
  const [fileErrors, setFileErrors] = useState<ErrorLogs | undefined>(undefined)

  const [launchSuccess, setLaunchSuccess] = useState<boolean>(false)
  const [launchError, setLaunchError] = useState<boolean>(false)
  const [launchInfo, setLaunchInfo] = useState<
    LaunchTraceabilityResponse | undefined
  >(undefined)
  const [traceabilityErrors, setTraceabilityErrors] = useState<AxiosError<
    ZodIssue | string
  > | null>(null)

  const formSchema = z.object({
    file: z.instanceof(File).nullable(),
  })

  type formType = z.infer<typeof formSchema>

  const form = useForm({
    validate: zodResolver(formSchema),
    initialValues: {
      email: traceabilityState.owner,
      file: null,
    } as formType,
  })

  useEffect(() => {
    setTraceabilityState(traceability)
  }, [traceability])

  const { mutate: launchtraceability, isLoading: isLaunching } =
    useLaunchTraceabilityMutation({
      onSuccess: (data) => {
        setLaunchSuccess(true)
        setLaunchInfo({
          irCount: data.irCount,
          supplierEmails: data.supplierEmails,
        })
      },
      onError: (error) => {
        console.error(error)
        setTraceabilityErrors(error)
        setLaunchError(true)
      },
    })

  const handleClose = () => {
    form.reset()
    setFileErrors(undefined)
    setLaunchInfo(undefined)
    setLaunchSuccess(false)
    setLaunchError(false)
    drawerActions.close()
  }

  const handleSubmit = (values: formType) => {
    launchtraceability({
      ingestionId: traceabilityState.id,
      clientEmail: traceabilityState.owner,
      file: values.file,
    })
    form.reset()
  }
  return (
    <Modal
      opened={modalOpened}
      onClose={() => handleClose()}
      title={
        <Text fz="xl" fw="bold">
          Traceability launch 🚀
        </Text>
      }
    >
      {!launchSuccess && !launchError ? (
        <>
          <LoadingOverlay
            visible={isLaunching}
            zIndex={1000}
            overlayProps={{ radius: "sm", blur: 2 }}
          />
          <form
            onSubmit={form.onSubmit((values, event) => {
              event?.preventDefault()
              handleSubmit(values)
            })}
          >
            <Stack>
              <InlineBanner
                type="info"
                message={`You are about to launch trceability requests for ${traceability.product_count} products`}
                closable={false}
              >
                <Text>
                  You are about to launch a traceability processus for file{" "}
                  <strong>{traceability?.document.name}</strong> for{" "}
                  <strong>{traceability?.product_count}</strong> products.
                  Emails will be sent on behalf of{" "}
                  <strong>{traceability.owner}</strong>
                </Text>
              </InlineBanner>
              <UploadOperationsForm
                setFileErrors={setFileErrors}
                fileErrors={fileErrors}
                form={form}
                clientEmail={traceability.owner}
              />
              <Group py="md">
                <Button
                  fullWidth={true}
                  type="submit"
                  disabled={!form.isValid() || !!fileErrors}
                >
                  Launch Traceability
                </Button>
              </Group>
            </Stack>
          </form>
        </>
      ) : launchSuccess ? (
        <TraceabilitySuccessStats
          launchInfo={launchInfo}
          handleClose={handleClose}
        />
      ) : (
        <TraceabilityErrors traceabilityErrors={traceabilityErrors} />
      )}
    </Modal>
  )
}
